import React, { useCallback } from 'react';
import { CLAUSE_TYPE_TERMINAL, type TerminalClause } from '@atlaskit/jql-ast';
import type { TriggerProps } from '@atlaskit/popup';
import removeFieldClauseFromJql from '@atlassian/jira-jql-utils/src/utils/remove-field-clause.tsx';
import type { useHydrationQueryLoaderQuery$data } from '@atlassian/jira-relay/src/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderUI$data } from '@atlassian/jira-relay/src/__generated__/JQLBuilderUI.graphql.ts';
import type { AdvancedContent$data } from '@atlassian/jira-relay/src/__generated__/AdvancedContent.graphql';
import type { OnSearchWrapper } from '../../../../common/types.tsx';
import PickerTrigger from '../../../../common/ui/picker-trigger/main.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import type { AnalyticsProps } from '../../../../utils/send-option-analytics.tsx';
import { CascadeSelectPickerPopup } from './main.tsx';
import { TriggerLabelContainer } from './trigger-label-container/index.tsx';
import { getUpdatedJql } from './utils/get-updated-jql.tsx';
import { useHydratedValues } from './utils/useHydratedValues.tsx';
import { useSelectedValues } from './utils/use-selected-values.tsx';

export type Props = {
	activePopupKey: string;
	clause: TerminalClause | undefined;
	fieldDescription: string | null;
	fieldId: string;
	fieldLabel: string | undefined;
	hydratedData: useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data;
	onClose: () => void;
	onOpen: () => void;
	onClear: (onClearJqlClause: string | undefined) => void;
	onSearchWrapper?: OnSearchWrapper;
	onSendOptionAnalytics?: (attributes: AnalyticsProps) => void;
	isIrremovable?: boolean;
	contextJql: string;
};

export const CascadeSelectPicker = ({
	activePopupKey,
	clause,
	fieldId,
	fieldLabel,
	fieldDescription,
	hydratedData,
	onClose,
	onOpen,
	onClear,
	onSearchWrapper,
	onSendOptionAnalytics,
	isIrremovable,
	contextJql,
}: Props): React.JSX.Element => {
	if (clause !== undefined && clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
		throw new Error(
			'cascadeSelectPicker attempted to hydrate using a query that should have been handled by query-too-complex',
		);
	}

	// match the hydrated value to the option from the clause & handle optimistic updates
	const selectedOptionsFromHydration = useHydratedValues({ fieldId, clause, hydratedData });

	const [selectedOption, setSelectedOption] = useSelectedValues(
		hydratedData,
		selectedOptionsFromHydration,
	);
	const [ast] = useAST();

	/**
	 * fieldLabel comes from field displayName from the hydration data.
	 * It is defined in the fragment `JQLEditorUIWithErrorBoundary` in
	 * `src/packages/jql-builder/basic/src/ui/jql-editor/index.tsx`
	 * we never expect it to be undefined but since we are not using the @required
	 * directive in the fragment, this check is required.
	 */
	const safeFieldLabel = fieldLabel ?? '';
	const triggerTextContent = {
		fieldLabel: safeFieldLabel,
		parentOption: selectedOption?.parentOption?.label,
		childOption: selectedOption?.childOption?.label,
	};
	const triggerHasValues = Boolean(
		triggerTextContent.parentOption || triggerTextContent.childOption,
	);
	const triggerHasInvalidValues = Boolean(
		selectedOption?.parentOption?.isInvalid || selectedOption?.childOption?.isInvalid,
	);

	const isOpen = activePopupKey === fieldId;
	const onTriggerClick = useCallback(
		() => (isOpen ? onClose() : onOpen()),
		[isOpen, onClose, onOpen],
	);

	const triggerRenderer = useCallback(
		(triggerProps: TriggerProps) => (
			<PickerTrigger
				{...triggerProps}
				fieldDescription={fieldDescription}
				isInvalid={triggerHasInvalidValues}
				isSelected={isOpen}
				isLoading={false}
				hasValues={triggerHasValues}
				onClear={() => onClear(clause ? removeFieldClauseFromJql(ast, clause) : undefined)}
				onClick={onTriggerClick}
				testId="jql-builder-basic.ui.jql-editor.field.cascade-select-picker.picker-trigger"
				isIrremovable={isIrremovable}
			>
				<TriggerLabelContainer
					fieldLabel={triggerTextContent.fieldLabel}
					parentValue={triggerTextContent.parentOption}
					childValue={triggerTextContent.childOption}
				/>
			</PickerTrigger>
		),
		[
			fieldDescription,
			triggerHasInvalidValues,
			isOpen,
			triggerHasValues,
			onTriggerClick,
			isIrremovable,
			triggerTextContent.fieldLabel,
			triggerTextContent.parentOption,
			triggerTextContent.childOption,
			onClear,
			clause,
			ast,
		],
	);

	return (
		<CascadeSelectPickerPopup
			fieldId={fieldId}
			initialValue={selectedOption}
			isOpen={isOpen}
			onClose={onClose}
			onChange={(newValue) => {
				setSelectedOption(newValue);
				onSendOptionAnalytics &&
					onSendOptionAnalytics({
						hasParentOption: Boolean(newValue.parentOption),
						hasChildOption: Boolean(newValue.childOption),
					});
				onSearchWrapper && onSearchWrapper(getUpdatedJql(ast, clause, fieldId, newValue));
				onClose();
			}}
			renderTrigger={triggerRenderer}
			contextJql={contextJql}
		/>
	);
};
